/*M///////////////////////////////////////////////////////////////////////////////////////
//
//  IMPORTANT: READ BEFORE DOWNLOADING, COPYING, INSTALLING OR USING.
//
//  By downloading, copying, installing or using the software you agree to this license.
//  If you do not agree to this license, do not download, install,
//  copy or use the software.
//
//
//                           License Agreement
//                For Open Source Computer Vision Library
//
// Copyright (C) 2013, OpenCV Foundation, all rights reserved.
// Third party copyrights are property of their respective owners.
//
// Redistribution and use in source and binary forms, with or without modification,
// are permitted provided that the following conditions are met:
//
//   * Redistribution's of source code must retain the above copyright notice,
//     this list of conditions and the following disclaimer.
//
//   * Redistribution's in binary form must reproduce the above copyright notice,
//     this list of conditions and the following disclaimer in the documentation
//     and/or other materials provided with the distribution.
//
//   * The name of the copyright holders may not be used to endorse or promote products
//     derived from this software without specific prior written permission.
//
// This software is provided by the copyright holders and contributors "as is" and
// any express or implied warranties, including, but not limited to, the implied
// warranties of merchantability and fitness for a particular purpose are disclaimed.
// In no event shall the Intel Corporation or contributors be liable for any direct,
// indirect, incidental, special, exemplary, or consequential damages
// (including, but not limited to, procurement of substitute goods or services;
// loss of use, data, or profits; or business interruption) however caused
// and on any theory of liability, whether in contract, strict liability,
// or tort (including negligence or otherwise) arising in any way out of
// the use of this software, even if advised of the possibility of such damage.
//
//M*/

/*M///////////////////////////////////////////////////////////////////////////////////////
// Author: Sajjad Taheri, University of California, Irvine. sajjadt[at]uci[dot]edu
//
//                             LICENSE AGREEMENT
// Copyright (c) 2015 The Regents of the University of California (Regents)
//
// Redistribution and use in source and binary forms, with or without
// modification, are permitted provided that the following conditions are met:
// 1. Redistributions of source code must retain the above copyright
//    notice, this list of conditions and the following disclaimer.
// 2. Redistributions in binary form must reproduce the above copyright
//    notice, this list of conditions and the following disclaimer in the
//    documentation and/or other materials provided with the distribution.
// 3. Neither the name of the University nor the
//    names of its contributors may be used to endorse or promote products
//    derived from this software without specific prior written permission.
//
// THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS ''AS IS'' AND ANY
// EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
// WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
// DISCLAIMED. IN NO EVENT SHALL CONTRIBUTORS BE LIABLE FOR ANY
// DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
// (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
// LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND
// ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
// (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
// SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
//M*/

#include "opencv2/core.hpp"
#include "opencv2/imgproc.hpp"
#include "opencv2/video/tracking.hpp"
#include "opencv2/video/background_segm.hpp"
#include "opencv2/objdetect.hpp"
#include "System.h"

#include <emscripten/bind.h>

using namespace emscripten;
using namespace cv;
using namespace ORB_SLAM2;

namespace binding_utils
{
    template<typename T>
    emscripten::val matData(const cv::Mat& mat)
    {
        return emscripten::val(emscripten::memory_view<T>((mat.total()*mat.elemSize())/sizeof(T),
                               (T*)mat.data));
    }

    template<typename T>
    emscripten::val matPtr(const cv::Mat& mat, int i)
    {
        return emscripten::val(emscripten::memory_view<T>(mat.step1(0), mat.ptr<T>(i)));
    }

    template<typename T>
    emscripten::val matPtr(const cv::Mat& mat, int i, int j)
    {
        return emscripten::val(emscripten::memory_view<T>(mat.step1(1), mat.ptr<T>(i,j)));
    }

    cv::Mat* createMat(int rows, int cols, int type, intptr_t data, size_t step)
    {
        return new cv::Mat(rows, cols, type, reinterpret_cast<void*>(data), step);
    }

    static emscripten::val getMatSize(const cv::Mat& mat)
    {
        emscripten::val size = emscripten::val::array();
        for (int i = 0; i < mat.dims; i++) {
            size.call<void>("push", mat.size[i]);
        }
        return size;
    }

    static emscripten::val getMatStep(const cv::Mat& mat)
    {
        emscripten::val step = emscripten::val::array();
        for (int i = 0; i < mat.dims; i++) {
            step.call<void>("push", mat.step[i]);
        }
        return step;
    }

    static Mat matEye(int rows, int cols, int type)
    {
        return Mat(cv::Mat::eye(rows, cols, type));
    }

    static Mat matEye(Size size, int type)
    {
        return Mat(cv::Mat::eye(size, type));
    }

    void convertTo(const Mat& obj, Mat& m, int rtype, double alpha, double beta)
    {
        obj.convertTo(m, rtype, alpha, beta);
    }

    void convertTo(const Mat& obj, Mat& m, int rtype)
    {
        obj.convertTo(m, rtype);
    }

    void convertTo(const Mat& obj, Mat& m, int rtype, double alpha)
    {
        obj.convertTo(m, rtype, alpha);
    }

    void cvtColor_wrapper(const cv::Mat& arg1, cv::Mat& arg2, int arg3, int arg4) {     
        return cv::cvtColor(arg1, arg2, arg3, arg4);      
    }     
          
    void cvtColor_wrapper_1(const cv::Mat& arg1, cv::Mat& arg2, int arg3) {       
        return cv::cvtColor(arg1, arg2, arg3);        
    }

    Size matSize(const cv::Mat& mat)
    {
        return mat.size();
    }

    cv::Mat matZeros(int arg0, int arg1, int arg2)
    {
        return cv::Mat::zeros(arg0, arg1, arg2);
    }

    cv::Mat matZeros(cv::Size arg0, int arg1)
    {
        return cv::Mat::zeros(arg0,arg1);
    }

    cv::Mat matOnes(int arg0, int arg1, int arg2)
    {
        return cv::Mat::ones(arg0, arg1, arg2);
    }

    cv::Mat matOnes(cv::Size arg0, int arg1)
    {
        return cv::Mat::ones(arg0, arg1);
    }

    double matDot(const cv::Mat& obj, const Mat& mat)
    {
        return  obj.dot(mat);
    }

    Mat matMul(const cv::Mat& obj, const Mat& mat, double scale)
    {
        return  Mat(obj.mul(mat, scale));
    }

    Mat matT(const cv::Mat& obj)
    {
        return  Mat(obj.t());
    }

    Mat matInv(const cv::Mat& obj, int type)
    {
        return  Mat(obj.inv(type));
    }

    void matCopyTo(const cv::Mat& obj, cv::Mat& mat)
    {
        return obj.copyTo(mat);
    }

    void matCopyTo(const cv::Mat& obj, cv::Mat& mat, const cv::Mat& mask)
    {
        return obj.copyTo(mat, mask);
    }

    Mat matDiag(const cv::Mat& obj, int d)
    {
        return obj.diag(d);
    }

    Mat matDiag(const cv::Mat& obj)
    {
        return obj.diag();
    }

    void matSetTo(cv::Mat& obj, const cv::Scalar& s)
    {
        obj.setTo(s);
    }

    void matSetTo(cv::Mat& obj, const cv::Scalar& s, const cv::Mat& mask)
    {
        obj.setTo(s, mask);
    }

    emscripten::val rotatedRectPoints(const cv::RotatedRect& obj)
    {
        cv::Point2f points[4];
        obj.points(points);
        emscripten::val pointsArray = emscripten::val::array();
        for (int i = 0; i < 4; i++) {
            pointsArray.call<void>("push", points[i]);
        }
        return pointsArray;
    }

    Rect rotatedRectBoundingRect(const cv::RotatedRect& obj)
    {
        return obj.boundingRect();
    }

    Rect2f rotatedRectBoundingRect2f(const cv::RotatedRect& obj)
    {
        return obj.boundingRect2f();
    }

    int cvMatDepth(int flags)
    {
        return CV_MAT_DEPTH(flags);
    }

    class MinMaxLoc
    {
    public:
        double minVal;
        double maxVal;
        Point minLoc;
        Point maxLoc;
    };

    MinMaxLoc minMaxLoc(const cv::Mat& src, const cv::Mat& mask)
    {
        MinMaxLoc result;
        cv::minMaxLoc(src, &result.minVal, &result.maxVal, &result.minLoc, &result.maxLoc, mask);
        return result;
    }

    MinMaxLoc minMaxLoc_1(const cv::Mat& src)
    {
        MinMaxLoc result;
        cv::minMaxLoc(src, &result.minVal, &result.maxVal, &result.minLoc, &result.maxLoc);
        return result;
    }

    class Circle
    {
    public:
        Point2f center;
        float radius;
    };

    Circle minEnclosingCircle(const cv::Mat& points)
    {
        Circle circle;
        cv::minEnclosingCircle(points, circle.center, circle.radius);
        return circle;
    }

    emscripten::val CamShiftWrapper(const cv::Mat& arg1, Rect& arg2, TermCriteria arg3)
    {
        RotatedRect rotatedRect = cv::CamShift(arg1, arg2, arg3);
        emscripten::val result = emscripten::val::array();
        result.call<void>("push", rotatedRect);
        result.call<void>("push", arg2);
        return result;
    }

    emscripten::val meanShiftWrapper(const cv::Mat& arg1, Rect& arg2, TermCriteria arg3)
    {
        int n = cv::meanShift(arg1, arg2, arg3);
        emscripten::val result = emscripten::val::array();
        result.call<void>("push", n);
        result.call<void>("push", arg2);
        return result;
    }

    ORB_SLAM2::System* createSystem(const string &strVocFile, const string &strSettingsFile, const int sensor, bool bUseViewer) {
        return new ORB_SLAM2::System(strVocFile, strSettingsFile, System::eSensor(sensor), bUseViewer);
    }
}

EMSCRIPTEN_BINDINGS(binding_utils)
{
    register_vector<int>("IntVector");
    register_vector<float>("FloatVector");
    register_vector<double>("DoubleVector");
    register_vector<cv::Point>("PointVector");
    register_vector<cv::Mat>("MatVector");
    register_vector<cv::Rect>("RectVector");

    emscripten::class_<cv::Mat>("Mat")
        .constructor<>()
        .constructor<const Mat&>()
        .constructor<Size, int>()
        .constructor<int, int, int>()
        .constructor<int, int, int, const Scalar&>()
        .constructor(&binding_utils::createMat, allow_raw_pointers())

        .class_function("eye", select_overload<Mat(int, int, int)>(&binding_utils::matEye))
        .class_function("eye", select_overload<Mat(Size, int)>(&binding_utils::matEye))
        .class_function("ones", select_overload<Mat(int, int, int)>(&binding_utils::matOnes))
        .class_function("ones", select_overload<Mat(Size, int)>(&binding_utils::matOnes))
        .class_function("zeros", select_overload<Mat(int, int, int)>(&binding_utils::matZeros))
        .class_function("zeros", select_overload<Mat(Size, int)>(&binding_utils::matZeros))

        .property("rows", &cv::Mat::rows)
        .property("cols", &cv::Mat::cols)
        .property("matSize", &binding_utils::getMatSize)
        .property("step", &binding_utils::getMatStep)
        .property("data", &binding_utils::matData<unsigned char>)
        .property("data8S", &binding_utils::matData<char>)
        .property("data16U", &binding_utils::matData<unsigned short>)
        .property("data16S", &binding_utils::matData<short>)
        .property("data32S", &binding_utils::matData<int>)
        .property("data32F", &binding_utils::matData<float>)
        .property("data64F", &binding_utils::matData<double>)

        .function("elemSize", select_overload<size_t()const>(&cv::Mat::elemSize))
        .function("elemSize1", select_overload<size_t()const>(&cv::Mat::elemSize1))
        .function("channels", select_overload<int()const>(&cv::Mat::channels))
        .function("convertTo", select_overload<void(const Mat&, Mat&, int, double, double)>(&binding_utils::convertTo))
        .function("convertTo", select_overload<void(const Mat&, Mat&, int)>(&binding_utils::convertTo))
        .function("convertTo", select_overload<void(const Mat&, Mat&, int, double)>(&binding_utils::convertTo))
        .function("total", select_overload<size_t()const>(&cv::Mat::total))
        .function("row", select_overload<Mat(int)const>(&cv::Mat::row))
        .function("create", select_overload<void(int, int, int)>(&cv::Mat::create))
        .function("create", select_overload<void(Size, int)>(&cv::Mat::create))
        .function("rowRange", select_overload<Mat(int, int)const>(&cv::Mat::rowRange))
        .function("rowRange", select_overload<Mat(const Range&)const>(&cv::Mat::rowRange))
        .function("copyTo", select_overload<void(const Mat&, Mat&)>(&binding_utils::matCopyTo))
        .function("copyTo", select_overload<void(const Mat&, Mat&, const Mat&)>(&binding_utils::matCopyTo))
        .function("type", select_overload<int()const>(&cv::Mat::type))
        .function("empty", select_overload<bool()const>(&cv::Mat::empty))
        .function("colRange", select_overload<Mat(int, int)const>(&cv::Mat::colRange))
        .function("colRange", select_overload<Mat(const Range&)const>(&cv::Mat::colRange))
        .function("step1", select_overload<size_t(int)const>(&cv::Mat::step1))
        .function("clone", select_overload<Mat()const>(&cv::Mat::clone))
        .function("depth", select_overload<int()const>(&cv::Mat::depth))
        .function("col", select_overload<Mat(int)const>(&cv::Mat::col))
        .function("dot", select_overload<double(const Mat&, const Mat&)>(&binding_utils::matDot))
        .function("mul", select_overload<Mat(const Mat&, const Mat&, double)>(&binding_utils::matMul))
        .function("inv", select_overload<Mat(const Mat&, int)>(&binding_utils::matInv))
        .function("t", select_overload<Mat(const Mat&)>(&binding_utils::matT))
        .function("roi", select_overload<Mat(const Rect&)const>(&cv::Mat::operator()))
        .function("diag", select_overload<Mat(const Mat&, int)>(&binding_utils::matDiag))
        .function("diag", select_overload<Mat(const Mat&)>(&binding_utils::matDiag))
        .function("isContinuous", select_overload<bool()const>(&cv::Mat::isContinuous))
        .function("setTo", select_overload<void(Mat&, const Scalar&)>(&binding_utils::matSetTo))
        .function("setTo", select_overload<void(Mat&, const Scalar&, const Mat&)>(&binding_utils::matSetTo))
        .function("size", select_overload<Size(const Mat&)>(&binding_utils::matSize))

        .function("ptr", select_overload<val(const Mat&, int)>(&binding_utils::matPtr<unsigned char>))
        .function("ptr", select_overload<val(const Mat&, int, int)>(&binding_utils::matPtr<unsigned char>))
        .function("ucharPtr", select_overload<val(const Mat&, int)>(&binding_utils::matPtr<unsigned char>))
        .function("ucharPtr", select_overload<val(const Mat&, int, int)>(&binding_utils::matPtr<unsigned char>))
        .function("charPtr", select_overload<val(const Mat&, int)>(&binding_utils::matPtr<char>))
        .function("charPtr", select_overload<val(const Mat&, int, int)>(&binding_utils::matPtr<char>))
        .function("shortPtr", select_overload<val(const Mat&, int)>(&binding_utils::matPtr<short>))
        .function("shortPtr", select_overload<val(const Mat&, int, int)>(&binding_utils::matPtr<short>))
        .function("ushortPtr", select_overload<val(const Mat&, int)>(&binding_utils::matPtr<unsigned short>))
        .function("ushortPtr", select_overload<val(const Mat&, int, int)>(&binding_utils::matPtr<unsigned short>))
        .function("intPtr", select_overload<val(const Mat&, int)>(&binding_utils::matPtr<int>))
        .function("intPtr", select_overload<val(const Mat&, int, int)>(&binding_utils::matPtr<int>))
        .function("floatPtr", select_overload<val(const Mat&, int)>(&binding_utils::matPtr<float>))
        .function("floatPtr", select_overload<val(const Mat&, int, int)>(&binding_utils::matPtr<float>))
        .function("doublePtr", select_overload<val(const Mat&, int)>(&binding_utils::matPtr<double>))
        .function("doublePtr", select_overload<val(const Mat&, int, int)>(&binding_utils::matPtr<double>))

        .function("charAt", select_overload<char&(int)>(&cv::Mat::at<char>))
        .function("charAt", select_overload<char&(int, int)>(&cv::Mat::at<char>))
        .function("charAt", select_overload<char&(int, int, int)>(&cv::Mat::at<char>))
        .function("ucharAt", select_overload<unsigned char&(int)>(&cv::Mat::at<unsigned char>))
        .function("ucharAt", select_overload<unsigned char&(int, int)>(&cv::Mat::at<unsigned char>))
        .function("ucharAt", select_overload<unsigned char&(int, int, int)>(&cv::Mat::at<unsigned char>))
        .function("shortAt", select_overload<short&(int)>(&cv::Mat::at<short>))
        .function("shortAt", select_overload<short&(int, int)>(&cv::Mat::at<short>))
        .function("shortAt", select_overload<short&(int, int, int)>(&cv::Mat::at<short>))
        .function("ushortAt", select_overload<unsigned short&(int)>(&cv::Mat::at<unsigned short>))
        .function("ushortAt", select_overload<unsigned short&(int, int)>(&cv::Mat::at<unsigned short>))
        .function("ushortAt", select_overload<unsigned short&(int, int, int)>(&cv::Mat::at<unsigned short>))
        .function("intAt", select_overload<int&(int)>(&cv::Mat::at<int>) )
        .function("intAt", select_overload<int&(int, int)>(&cv::Mat::at<int>) )
        .function("intAt", select_overload<int&(int, int, int)>(&cv::Mat::at<int>) )
        .function("floatAt", select_overload<float&(int)>(&cv::Mat::at<float>))
        .function("floatAt", select_overload<float&(int, int)>(&cv::Mat::at<float>))
        .function("floatAt", select_overload<float&(int, int, int)>(&cv::Mat::at<float>))
        .function("doubleAt", select_overload<double&(int, int, int)>(&cv::Mat::at<double>))
        .function("doubleAt", select_overload<double&(int)>(&cv::Mat::at<double>))
        .function("doubleAt", select_overload<double&(int, int)>(&cv::Mat::at<double>));

    emscripten::value_object<cv::Range>("Range")
        .field("start", &cv::Range::start)
        .field("end", &cv::Range::end);

    emscripten::value_object<cv::TermCriteria>("TermCriteria")
        .field("type", &cv::TermCriteria::type)
        .field("maxCount", &cv::TermCriteria::maxCount)
        .field("epsilon", &cv::TermCriteria::epsilon);

#define EMSCRIPTEN_CV_SIZE(type) \
    emscripten::value_object<type>("#type") \
        .field("width", &type::width) \
        .field("height", &type::height);

    EMSCRIPTEN_CV_SIZE(Size)
    EMSCRIPTEN_CV_SIZE(Size2f)

#define EMSCRIPTEN_CV_POINT(type) \
    emscripten::value_object<type>("#type") \
        .field("x", &type::x) \
        .field("y", &type::y); \

    EMSCRIPTEN_CV_POINT(Point)
    EMSCRIPTEN_CV_POINT(Point2f)

#define EMSCRIPTEN_CV_RECT(type, name) \
    emscripten::value_object<cv::Rect_<type>> (name) \
        .field("x", &cv::Rect_<type>::x) \
        .field("y", &cv::Rect_<type>::y) \
        .field("width", &cv::Rect_<type>::width) \
        .field("height", &cv::Rect_<type>::height);

    EMSCRIPTEN_CV_RECT(int, "Rect")
    EMSCRIPTEN_CV_RECT(float, "Rect2f")

    emscripten::value_object<cv::RotatedRect>("RotatedRect")
        .field("center", &cv::RotatedRect::center)
        .field("size", &cv::RotatedRect::size)
        .field("angle", &cv::RotatedRect::angle);

    emscripten::function("rotatedRectPoints", select_overload<emscripten::val(const cv::RotatedRect&)>(&binding_utils::rotatedRectPoints));
    emscripten::function("rotatedRectBoundingRect", select_overload<Rect(const cv::RotatedRect&)>(&binding_utils::rotatedRectBoundingRect));
    emscripten::function("rotatedRectBoundingRect2f", select_overload<Rect2f(const cv::RotatedRect&)>(&binding_utils::rotatedRectBoundingRect2f));

    emscripten::value_array<cv::Scalar_<double>> ("Scalar")
        .element(index<0>())
        .element(index<1>())
        .element(index<2>())
        .element(index<3>());

    emscripten::value_object<binding_utils::MinMaxLoc>("MinMaxLoc")
        .field("minVal", &binding_utils::MinMaxLoc::minVal)
        .field("maxVal", &binding_utils::MinMaxLoc::maxVal)
        .field("minLoc", &binding_utils::MinMaxLoc::minLoc)
        .field("maxLoc", &binding_utils::MinMaxLoc::maxLoc);

    emscripten::value_object<binding_utils::Circle>("Circle")
        .field("center", &binding_utils::Circle::center)
        .field("radius", &binding_utils::Circle::radius);

    emscripten::value_object<cv::Moments >("Moments")
        .field("m00", &cv::Moments::m00)
        .field("m10", &cv::Moments::m10)
        .field("m01", &cv::Moments::m01)
        .field("m20", &cv::Moments::m20)
        .field("m11", &cv::Moments::m11)
        .field("m02", &cv::Moments::m02)
        .field("m30", &cv::Moments::m30)
        .field("m21", &cv::Moments::m21)
        .field("m12", &cv::Moments::m12)
        .field("m03", &cv::Moments::m03)
        .field("mu20", &cv::Moments::mu20)
        .field("mu11", &cv::Moments::mu11)
        .field("mu02", &cv::Moments::mu02)
        .field("mu30", &cv::Moments::mu30)
        .field("mu21", &cv::Moments::mu21)
        .field("mu12", &cv::Moments::mu12)
        .field("mu03", &cv::Moments::mu03)
        .field("nu20", &cv::Moments::nu20)
        .field("nu11", &cv::Moments::nu11)
        .field("nu02", &cv::Moments::nu02)
        .field("nu30", &cv::Moments::nu30)
        .field("nu21", &cv::Moments::nu21)
        .field("nu12", &cv::Moments::nu12)
        .field("nu03", &cv::Moments::nu03);

    emscripten::function("minEnclosingCircle", select_overload<binding_utils::Circle(const cv::Mat&)>(&binding_utils::minEnclosingCircle));

    emscripten::function("minMaxLoc", select_overload<binding_utils::MinMaxLoc(const cv::Mat&, const cv::Mat&)>(&binding_utils::minMaxLoc));

    emscripten::function("minMaxLoc", select_overload<binding_utils::MinMaxLoc(const cv::Mat&)>(&binding_utils::minMaxLoc_1));

    emscripten::function("morphologyDefaultBorderValue", &cv::morphologyDefaultBorderValue);

    emscripten::function("CV_MAT_DEPTH", &binding_utils::cvMatDepth);

    emscripten::function("CamShift", select_overload<emscripten::val(const cv::Mat&, Rect&, TermCriteria)>(&binding_utils::CamShiftWrapper));

    emscripten::function("meanShift", select_overload<emscripten::val(const cv::Mat&, Rect&, TermCriteria)>(&binding_utils::meanShiftWrapper));

    emscripten::function("cvtColor", select_overload<void(const cv::Mat&, cv::Mat&, int, int)>(&binding_utils::cvtColor_wrapper));     
      
    emscripten::function("cvtColor", select_overload<void(const cv::Mat&, cv::Mat&, int)>(&binding_utils::cvtColor_wrapper_1));       

    constant("CV_8UC1", CV_8UC1);
    constant("CV_8UC2", CV_8UC2);
    constant("CV_8UC3", CV_8UC3);
    constant("CV_8UC4", CV_8UC4);

    constant("CV_8SC1", CV_8SC1);
    constant("CV_8SC2", CV_8SC2);
    constant("CV_8SC3", CV_8SC3);
    constant("CV_8SC4", CV_8SC4);

    constant("CV_16UC1", CV_16UC1);
    constant("CV_16UC2", CV_16UC2);
    constant("CV_16UC3", CV_16UC3);
    constant("CV_16UC4", CV_16UC4);

    constant("CV_16SC1", CV_16SC1);
    constant("CV_16SC2", CV_16SC2);
    constant("CV_16SC3", CV_16SC3);
    constant("CV_16SC4", CV_16SC4);

    constant("CV_32SC1", CV_32SC1);
    constant("CV_32SC2", CV_32SC2);
    constant("CV_32SC3", CV_32SC3);
    constant("CV_32SC4", CV_32SC4);

    constant("CV_32FC1", CV_32FC1);
    constant("CV_32FC2", CV_32FC2);
    constant("CV_32FC3", CV_32FC3);
    constant("CV_32FC4", CV_32FC4);

    constant("CV_64FC1", CV_64FC1);
    constant("CV_64FC2", CV_64FC2);
    constant("CV_64FC3", CV_64FC3);
    constant("CV_64FC4", CV_64FC4);

    constant("CV_8U", CV_8U);
    constant("CV_8S", CV_8S);
    constant("CV_16U", CV_16U);
    constant("CV_16S", CV_16S);
    constant("CV_32S",  CV_32S);
    constant("CV_32F", CV_32F);
    constant("CV_64F", CV_64F);

    constant("INT_MIN", INT_MIN);
    constant("INT_MAX", INT_MAX);

    // ORB_SLAM2 bindings
    constant("MONOCULAR", +System::MONOCULAR);
    constant("STEREO", +System::STEREO);
    constant("RGBD", +System::RGBD);

    register_vector<cv::KeyPoint>("KeyPointVector");
    register_vector<ORB_SLAM2::MapPoint*>("MapPointVector");

    emscripten::class_<ORB_SLAM2::System>("SLAM")
        .constructor(&binding_utils::createSystem)
        .function("TrackStereo", &ORB_SLAM2::System::TrackStereo)
        .function("TrackRGBD", &ORB_SLAM2::System::TrackRGBD)
        .function("TrackMixRGBD", &ORB_SLAM2::System::TrackMixRGBD)
        .function("TrackMonocular", &ORB_SLAM2::System::TrackMonocular)
        .function("ActivateLocalizationMode", &ORB_SLAM2::System::ActivateLocalizationMode)
        .function("DeactivateLocalizationMode", &ORB_SLAM2::System::DeactivateLocalizationMode)
        .function("MapChanged", &ORB_SLAM2::System::MapChanged)
        .function("Reset", &ORB_SLAM2::System::Reset)
        .function("Shutdown", &ORB_SLAM2::System::Shutdown)
        .function("GetTrackingState", &ORB_SLAM2::System::GetTrackingState)
        .function("GetTrackedMapPoints", &ORB_SLAM2::System::GetTrackedMapPoints)
        .function("GetTrackedKeyPointsUn", &ORB_SLAM2::System::GetTrackedKeyPointsUn)
        .function("GetCurrentFrame", &ORB_SLAM2::System::GetCurrentFrame)
        .function("GetKeyFramesInMap", &ORB_SLAM2::System::GetKeyFramesInMap)
        .function("GetMapPointsInMap", &ORB_SLAM2::System::GetMapPointsInMap);
}
namespace Wrappers {

}

EMSCRIPTEN_BINDINGS(testBinding) {
    constant("ACCESS_FAST", +cv::ACCESS_FAST);

    constant("ACCESS_MASK", +cv::ACCESS_MASK);

    constant("ACCESS_READ", +cv::ACCESS_READ);

    constant("ACCESS_RW", +cv::ACCESS_RW);

    constant("ACCESS_WRITE", +cv::ACCESS_WRITE);

    constant("ADAPTIVE_THRESH_GAUSSIAN_C", +cv::ADAPTIVE_THRESH_GAUSSIAN_C);

    constant("ADAPTIVE_THRESH_MEAN_C", +cv::ADAPTIVE_THRESH_MEAN_C);

    constant("BORDER_CONSTANT", +cv::BORDER_CONSTANT);

    constant("BORDER_DEFAULT", +cv::BORDER_DEFAULT);

    constant("BORDER_ISOLATED", +cv::BORDER_ISOLATED);

    constant("BORDER_REFLECT", +cv::BORDER_REFLECT);

    constant("BORDER_REFLECT101", +cv::BORDER_REFLECT101);

    constant("BORDER_REFLECT_101", +cv::BORDER_REFLECT_101);

    constant("BORDER_REPLICATE", +cv::BORDER_REPLICATE);

    constant("BORDER_TRANSPARENT", +cv::BORDER_TRANSPARENT);

    constant("BORDER_WRAP", +cv::BORDER_WRAP);

    constant("CASCADE_DO_CANNY_PRUNING", +cv::CASCADE_DO_CANNY_PRUNING);

    constant("CASCADE_DO_ROUGH_SEARCH", +cv::CASCADE_DO_ROUGH_SEARCH);

    constant("CASCADE_FIND_BIGGEST_OBJECT", +cv::CASCADE_FIND_BIGGEST_OBJECT);

    constant("CASCADE_SCALE_IMAGE", +cv::CASCADE_SCALE_IMAGE);

    constant("CCL_DEFAULT", +cv::CCL_DEFAULT);

    constant("CCL_GRANA", +cv::CCL_GRANA);

    constant("CCL_WU", +cv::CCL_WU);

    constant("CC_STAT_AREA", +cv::CC_STAT_AREA);

    constant("CC_STAT_HEIGHT", +cv::CC_STAT_HEIGHT);

    constant("CC_STAT_LEFT", +cv::CC_STAT_LEFT);

    constant("CC_STAT_MAX", +cv::CC_STAT_MAX);

    constant("CC_STAT_TOP", +cv::CC_STAT_TOP);

    constant("CC_STAT_WIDTH", +cv::CC_STAT_WIDTH);

    constant("CHAIN_APPROX_NONE", +cv::CHAIN_APPROX_NONE);

    constant("CHAIN_APPROX_SIMPLE", +cv::CHAIN_APPROX_SIMPLE);

    constant("CHAIN_APPROX_TC89_KCOS", +cv::CHAIN_APPROX_TC89_KCOS);

    constant("CHAIN_APPROX_TC89_L1", +cv::CHAIN_APPROX_TC89_L1);

    constant("CMP_EQ", +cv::CMP_EQ);

    constant("CMP_GE", +cv::CMP_GE);

    constant("CMP_GT", +cv::CMP_GT);

    constant("CMP_LE", +cv::CMP_LE);

    constant("CMP_LT", +cv::CMP_LT);

    constant("CMP_NE", +cv::CMP_NE);

    constant("COLORMAP_AUTUMN", +cv::COLORMAP_AUTUMN);

    constant("COLORMAP_BONE", +cv::COLORMAP_BONE);

    constant("COLORMAP_COOL", +cv::COLORMAP_COOL);

    constant("COLORMAP_HOT", +cv::COLORMAP_HOT);

    constant("COLORMAP_HSV", +cv::COLORMAP_HSV);

    constant("COLORMAP_JET", +cv::COLORMAP_JET);

    constant("COLORMAP_OCEAN", +cv::COLORMAP_OCEAN);

    constant("COLORMAP_PARULA", +cv::COLORMAP_PARULA);

    constant("COLORMAP_PINK", +cv::COLORMAP_PINK);

    constant("COLORMAP_RAINBOW", +cv::COLORMAP_RAINBOW);

    constant("COLORMAP_SPRING", +cv::COLORMAP_SPRING);

    constant("COLORMAP_SUMMER", +cv::COLORMAP_SUMMER);

    constant("COLORMAP_WINTER", +cv::COLORMAP_WINTER);

    constant("COLOR_BGR2BGR555", +cv::COLOR_BGR2BGR555);

    constant("COLOR_BGR2BGR565", +cv::COLOR_BGR2BGR565);

    constant("COLOR_BGR2BGRA", +cv::COLOR_BGR2BGRA);

    constant("COLOR_BGR2GRAY", +cv::COLOR_BGR2GRAY);

    constant("COLOR_BGR2HLS", +cv::COLOR_BGR2HLS);

    constant("COLOR_BGR2HLS_FULL", +cv::COLOR_BGR2HLS_FULL);

    constant("COLOR_BGR2HSV", +cv::COLOR_BGR2HSV);

    constant("COLOR_BGR2HSV_FULL", +cv::COLOR_BGR2HSV_FULL);

    constant("COLOR_BGR2Lab", +cv::COLOR_BGR2Lab);

    constant("COLOR_BGR2Luv", +cv::COLOR_BGR2Luv);

    constant("COLOR_BGR2RGB", +cv::COLOR_BGR2RGB);

    constant("COLOR_BGR2RGBA", +cv::COLOR_BGR2RGBA);

    constant("COLOR_BGR2XYZ", +cv::COLOR_BGR2XYZ);

    constant("COLOR_BGR2YCrCb", +cv::COLOR_BGR2YCrCb);

    constant("COLOR_BGR2YUV", +cv::COLOR_BGR2YUV);

    constant("COLOR_BGR2YUV_I420", +cv::COLOR_BGR2YUV_I420);

    constant("COLOR_BGR2YUV_IYUV", +cv::COLOR_BGR2YUV_IYUV);

    constant("COLOR_BGR2YUV_YV12", +cv::COLOR_BGR2YUV_YV12);

    constant("COLOR_BGR5552BGR", +cv::COLOR_BGR5552BGR);

    constant("COLOR_BGR5552BGRA", +cv::COLOR_BGR5552BGRA);

    constant("COLOR_BGR5552GRAY", +cv::COLOR_BGR5552GRAY);

    constant("COLOR_BGR5552RGB", +cv::COLOR_BGR5552RGB);

    constant("COLOR_BGR5552RGBA", +cv::COLOR_BGR5552RGBA);

    constant("COLOR_BGR5652BGR", +cv::COLOR_BGR5652BGR);

    constant("COLOR_BGR5652BGRA", +cv::COLOR_BGR5652BGRA);

    constant("COLOR_BGR5652GRAY", +cv::COLOR_BGR5652GRAY);

    constant("COLOR_BGR5652RGB", +cv::COLOR_BGR5652RGB);

    constant("COLOR_BGR5652RGBA", +cv::COLOR_BGR5652RGBA);

    constant("COLOR_BGRA2BGR", +cv::COLOR_BGRA2BGR);

    constant("COLOR_BGRA2BGR555", +cv::COLOR_BGRA2BGR555);

    constant("COLOR_BGRA2BGR565", +cv::COLOR_BGRA2BGR565);

    constant("COLOR_BGRA2GRAY", +cv::COLOR_BGRA2GRAY);

    constant("COLOR_BGRA2RGB", +cv::COLOR_BGRA2RGB);

    constant("COLOR_BGRA2RGBA", +cv::COLOR_BGRA2RGBA);

    constant("COLOR_BGRA2YUV_I420", +cv::COLOR_BGRA2YUV_I420);

    constant("COLOR_BGRA2YUV_IYUV", +cv::COLOR_BGRA2YUV_IYUV);

    constant("COLOR_BGRA2YUV_YV12", +cv::COLOR_BGRA2YUV_YV12);

    constant("COLOR_BayerBG2BGR", +cv::COLOR_BayerBG2BGR);

    constant("COLOR_BayerBG2BGRA", +cv::COLOR_BayerBG2BGRA);

    constant("COLOR_BayerBG2BGR_EA", +cv::COLOR_BayerBG2BGR_EA);

    constant("COLOR_BayerBG2BGR_VNG", +cv::COLOR_BayerBG2BGR_VNG);

    constant("COLOR_BayerBG2GRAY", +cv::COLOR_BayerBG2GRAY);

    constant("COLOR_BayerBG2RGB", +cv::COLOR_BayerBG2RGB);

    constant("COLOR_BayerBG2RGBA", +cv::COLOR_BayerBG2RGBA);

    constant("COLOR_BayerBG2RGB_EA", +cv::COLOR_BayerBG2RGB_EA);

    constant("COLOR_BayerBG2RGB_VNG", +cv::COLOR_BayerBG2RGB_VNG);

    constant("COLOR_BayerGB2BGR", +cv::COLOR_BayerGB2BGR);

    constant("COLOR_BayerGB2BGRA", +cv::COLOR_BayerGB2BGRA);

    constant("COLOR_BayerGB2BGR_EA", +cv::COLOR_BayerGB2BGR_EA);

    constant("COLOR_BayerGB2BGR_VNG", +cv::COLOR_BayerGB2BGR_VNG);

    constant("COLOR_BayerGB2GRAY", +cv::COLOR_BayerGB2GRAY);

    constant("COLOR_BayerGB2RGB", +cv::COLOR_BayerGB2RGB);

    constant("COLOR_BayerGB2RGBA", +cv::COLOR_BayerGB2RGBA);

    constant("COLOR_BayerGB2RGB_EA", +cv::COLOR_BayerGB2RGB_EA);

    constant("COLOR_BayerGB2RGB_VNG", +cv::COLOR_BayerGB2RGB_VNG);

    constant("COLOR_BayerGR2BGR", +cv::COLOR_BayerGR2BGR);

    constant("COLOR_BayerGR2BGRA", +cv::COLOR_BayerGR2BGRA);

    constant("COLOR_BayerGR2BGR_EA", +cv::COLOR_BayerGR2BGR_EA);

    constant("COLOR_BayerGR2BGR_VNG", +cv::COLOR_BayerGR2BGR_VNG);

    constant("COLOR_BayerGR2GRAY", +cv::COLOR_BayerGR2GRAY);

    constant("COLOR_BayerGR2RGB", +cv::COLOR_BayerGR2RGB);

    constant("COLOR_BayerGR2RGBA", +cv::COLOR_BayerGR2RGBA);

    constant("COLOR_BayerGR2RGB_EA", +cv::COLOR_BayerGR2RGB_EA);

    constant("COLOR_BayerGR2RGB_VNG", +cv::COLOR_BayerGR2RGB_VNG);

    constant("COLOR_BayerRG2BGR", +cv::COLOR_BayerRG2BGR);

    constant("COLOR_BayerRG2BGRA", +cv::COLOR_BayerRG2BGRA);

    constant("COLOR_BayerRG2BGR_EA", +cv::COLOR_BayerRG2BGR_EA);

    constant("COLOR_BayerRG2BGR_VNG", +cv::COLOR_BayerRG2BGR_VNG);

    constant("COLOR_BayerRG2GRAY", +cv::COLOR_BayerRG2GRAY);

    constant("COLOR_BayerRG2RGB", +cv::COLOR_BayerRG2RGB);

    constant("COLOR_BayerRG2RGBA", +cv::COLOR_BayerRG2RGBA);

    constant("COLOR_BayerRG2RGB_EA", +cv::COLOR_BayerRG2RGB_EA);

    constant("COLOR_BayerRG2RGB_VNG", +cv::COLOR_BayerRG2RGB_VNG);

    constant("COLOR_COLORCVT_MAX", +cv::COLOR_COLORCVT_MAX);

    constant("COLOR_GRAY2BGR", +cv::COLOR_GRAY2BGR);

    constant("COLOR_GRAY2BGR555", +cv::COLOR_GRAY2BGR555);

    constant("COLOR_GRAY2BGR565", +cv::COLOR_GRAY2BGR565);

    constant("COLOR_GRAY2BGRA", +cv::COLOR_GRAY2BGRA);

    constant("COLOR_GRAY2RGB", +cv::COLOR_GRAY2RGB);

    constant("COLOR_GRAY2RGBA", +cv::COLOR_GRAY2RGBA);

    constant("COLOR_HLS2BGR", +cv::COLOR_HLS2BGR);

    constant("COLOR_HLS2BGR_FULL", +cv::COLOR_HLS2BGR_FULL);

    constant("COLOR_HLS2RGB", +cv::COLOR_HLS2RGB);

    constant("COLOR_HLS2RGB_FULL", +cv::COLOR_HLS2RGB_FULL);

    constant("COLOR_HSV2BGR", +cv::COLOR_HSV2BGR);

    constant("COLOR_HSV2BGR_FULL", +cv::COLOR_HSV2BGR_FULL);

    constant("COLOR_HSV2RGB", +cv::COLOR_HSV2RGB);

    constant("COLOR_HSV2RGB_FULL", +cv::COLOR_HSV2RGB_FULL);

    constant("COLOR_LBGR2Lab", +cv::COLOR_LBGR2Lab);

    constant("COLOR_LBGR2Luv", +cv::COLOR_LBGR2Luv);

    constant("COLOR_LRGB2Lab", +cv::COLOR_LRGB2Lab);

    constant("COLOR_LRGB2Luv", +cv::COLOR_LRGB2Luv);

    constant("COLOR_Lab2BGR", +cv::COLOR_Lab2BGR);

    constant("COLOR_Lab2LBGR", +cv::COLOR_Lab2LBGR);

    constant("COLOR_Lab2LRGB", +cv::COLOR_Lab2LRGB);

    constant("COLOR_Lab2RGB", +cv::COLOR_Lab2RGB);

    constant("COLOR_Luv2BGR", +cv::COLOR_Luv2BGR);

    constant("COLOR_Luv2LBGR", +cv::COLOR_Luv2LBGR);

    constant("COLOR_Luv2LRGB", +cv::COLOR_Luv2LRGB);

    constant("COLOR_Luv2RGB", +cv::COLOR_Luv2RGB);

    constant("COLOR_RGB2BGR", +cv::COLOR_RGB2BGR);

    constant("COLOR_RGB2BGR555", +cv::COLOR_RGB2BGR555);

    constant("COLOR_RGB2BGR565", +cv::COLOR_RGB2BGR565);

    constant("COLOR_RGB2BGRA", +cv::COLOR_RGB2BGRA);

    constant("COLOR_RGB2GRAY", +cv::COLOR_RGB2GRAY);

    constant("COLOR_RGB2HLS", +cv::COLOR_RGB2HLS);

    constant("COLOR_RGB2HLS_FULL", +cv::COLOR_RGB2HLS_FULL);

    constant("COLOR_RGB2HSV", +cv::COLOR_RGB2HSV);

    constant("COLOR_RGB2HSV_FULL", +cv::COLOR_RGB2HSV_FULL);

    constant("COLOR_RGB2Lab", +cv::COLOR_RGB2Lab);

    constant("COLOR_RGB2Luv", +cv::COLOR_RGB2Luv);

    constant("COLOR_RGB2RGBA", +cv::COLOR_RGB2RGBA);

    constant("COLOR_RGB2XYZ", +cv::COLOR_RGB2XYZ);

    constant("COLOR_RGB2YCrCb", +cv::COLOR_RGB2YCrCb);

    constant("COLOR_RGB2YUV", +cv::COLOR_RGB2YUV);

    constant("COLOR_RGB2YUV_I420", +cv::COLOR_RGB2YUV_I420);

    constant("COLOR_RGB2YUV_IYUV", +cv::COLOR_RGB2YUV_IYUV);

    constant("COLOR_RGB2YUV_YV12", +cv::COLOR_RGB2YUV_YV12);

    constant("COLOR_RGBA2BGR", +cv::COLOR_RGBA2BGR);

    constant("COLOR_RGBA2BGR555", +cv::COLOR_RGBA2BGR555);

    constant("COLOR_RGBA2BGR565", +cv::COLOR_RGBA2BGR565);

    constant("COLOR_RGBA2BGRA", +cv::COLOR_RGBA2BGRA);

    constant("COLOR_RGBA2GRAY", +cv::COLOR_RGBA2GRAY);

    constant("COLOR_RGBA2RGB", +cv::COLOR_RGBA2RGB);

    constant("COLOR_RGBA2YUV_I420", +cv::COLOR_RGBA2YUV_I420);

    constant("COLOR_RGBA2YUV_IYUV", +cv::COLOR_RGBA2YUV_IYUV);

    constant("COLOR_RGBA2YUV_YV12", +cv::COLOR_RGBA2YUV_YV12);

    constant("COLOR_RGBA2mRGBA", +cv::COLOR_RGBA2mRGBA);

    constant("COLOR_XYZ2BGR", +cv::COLOR_XYZ2BGR);

    constant("COLOR_XYZ2RGB", +cv::COLOR_XYZ2RGB);

    constant("COLOR_YCrCb2BGR", +cv::COLOR_YCrCb2BGR);

    constant("COLOR_YCrCb2RGB", +cv::COLOR_YCrCb2RGB);

    constant("COLOR_YUV2BGR", +cv::COLOR_YUV2BGR);

    constant("COLOR_YUV2BGRA_I420", +cv::COLOR_YUV2BGRA_I420);

    constant("COLOR_YUV2BGRA_IYUV", +cv::COLOR_YUV2BGRA_IYUV);

    constant("COLOR_YUV2BGRA_NV12", +cv::COLOR_YUV2BGRA_NV12);

    constant("COLOR_YUV2BGRA_NV21", +cv::COLOR_YUV2BGRA_NV21);

    constant("COLOR_YUV2BGRA_UYNV", +cv::COLOR_YUV2BGRA_UYNV);

    constant("COLOR_YUV2BGRA_UYVY", +cv::COLOR_YUV2BGRA_UYVY);

    constant("COLOR_YUV2BGRA_Y422", +cv::COLOR_YUV2BGRA_Y422);

    constant("COLOR_YUV2BGRA_YUNV", +cv::COLOR_YUV2BGRA_YUNV);

    constant("COLOR_YUV2BGRA_YUY2", +cv::COLOR_YUV2BGRA_YUY2);

    constant("COLOR_YUV2BGRA_YUYV", +cv::COLOR_YUV2BGRA_YUYV);

    constant("COLOR_YUV2BGRA_YV12", +cv::COLOR_YUV2BGRA_YV12);

    constant("COLOR_YUV2BGRA_YVYU", +cv::COLOR_YUV2BGRA_YVYU);

    constant("COLOR_YUV2BGR_I420", +cv::COLOR_YUV2BGR_I420);

    constant("COLOR_YUV2BGR_IYUV", +cv::COLOR_YUV2BGR_IYUV);

    constant("COLOR_YUV2BGR_NV12", +cv::COLOR_YUV2BGR_NV12);

    constant("COLOR_YUV2BGR_NV21", +cv::COLOR_YUV2BGR_NV21);

    constant("COLOR_YUV2BGR_UYNV", +cv::COLOR_YUV2BGR_UYNV);

    constant("COLOR_YUV2BGR_UYVY", +cv::COLOR_YUV2BGR_UYVY);

    constant("COLOR_YUV2BGR_Y422", +cv::COLOR_YUV2BGR_Y422);

    constant("COLOR_YUV2BGR_YUNV", +cv::COLOR_YUV2BGR_YUNV);

    constant("COLOR_YUV2BGR_YUY2", +cv::COLOR_YUV2BGR_YUY2);

    constant("COLOR_YUV2BGR_YUYV", +cv::COLOR_YUV2BGR_YUYV);

    constant("COLOR_YUV2BGR_YV12", +cv::COLOR_YUV2BGR_YV12);

    constant("COLOR_YUV2BGR_YVYU", +cv::COLOR_YUV2BGR_YVYU);

    constant("COLOR_YUV2GRAY_420", +cv::COLOR_YUV2GRAY_420);

    constant("COLOR_YUV2GRAY_I420", +cv::COLOR_YUV2GRAY_I420);

    constant("COLOR_YUV2GRAY_IYUV", +cv::COLOR_YUV2GRAY_IYUV);

    constant("COLOR_YUV2GRAY_NV12", +cv::COLOR_YUV2GRAY_NV12);

    constant("COLOR_YUV2GRAY_NV21", +cv::COLOR_YUV2GRAY_NV21);

    constant("COLOR_YUV2GRAY_UYNV", +cv::COLOR_YUV2GRAY_UYNV);

    constant("COLOR_YUV2GRAY_UYVY", +cv::COLOR_YUV2GRAY_UYVY);

    constant("COLOR_YUV2GRAY_Y422", +cv::COLOR_YUV2GRAY_Y422);

    constant("COLOR_YUV2GRAY_YUNV", +cv::COLOR_YUV2GRAY_YUNV);

    constant("COLOR_YUV2GRAY_YUY2", +cv::COLOR_YUV2GRAY_YUY2);

    constant("COLOR_YUV2GRAY_YUYV", +cv::COLOR_YUV2GRAY_YUYV);

    constant("COLOR_YUV2GRAY_YV12", +cv::COLOR_YUV2GRAY_YV12);

    constant("COLOR_YUV2GRAY_YVYU", +cv::COLOR_YUV2GRAY_YVYU);

    constant("COLOR_YUV2RGB", +cv::COLOR_YUV2RGB);

    constant("COLOR_YUV2RGBA_I420", +cv::COLOR_YUV2RGBA_I420);

    constant("COLOR_YUV2RGBA_IYUV", +cv::COLOR_YUV2RGBA_IYUV);

    constant("COLOR_YUV2RGBA_NV12", +cv::COLOR_YUV2RGBA_NV12);

    constant("COLOR_YUV2RGBA_NV21", +cv::COLOR_YUV2RGBA_NV21);

    constant("COLOR_YUV2RGBA_UYNV", +cv::COLOR_YUV2RGBA_UYNV);

    constant("COLOR_YUV2RGBA_UYVY", +cv::COLOR_YUV2RGBA_UYVY);

    constant("COLOR_YUV2RGBA_Y422", +cv::COLOR_YUV2RGBA_Y422);

    constant("COLOR_YUV2RGBA_YUNV", +cv::COLOR_YUV2RGBA_YUNV);

    constant("COLOR_YUV2RGBA_YUY2", +cv::COLOR_YUV2RGBA_YUY2);

    constant("COLOR_YUV2RGBA_YUYV", +cv::COLOR_YUV2RGBA_YUYV);

    constant("COLOR_YUV2RGBA_YV12", +cv::COLOR_YUV2RGBA_YV12);

    constant("COLOR_YUV2RGBA_YVYU", +cv::COLOR_YUV2RGBA_YVYU);

    constant("COLOR_YUV2RGB_I420", +cv::COLOR_YUV2RGB_I420);

    constant("COLOR_YUV2RGB_IYUV", +cv::COLOR_YUV2RGB_IYUV);

    constant("COLOR_YUV2RGB_NV12", +cv::COLOR_YUV2RGB_NV12);

    constant("COLOR_YUV2RGB_NV21", +cv::COLOR_YUV2RGB_NV21);

    constant("COLOR_YUV2RGB_UYNV", +cv::COLOR_YUV2RGB_UYNV);

    constant("COLOR_YUV2RGB_UYVY", +cv::COLOR_YUV2RGB_UYVY);

    constant("COLOR_YUV2RGB_Y422", +cv::COLOR_YUV2RGB_Y422);

    constant("COLOR_YUV2RGB_YUNV", +cv::COLOR_YUV2RGB_YUNV);

    constant("COLOR_YUV2RGB_YUY2", +cv::COLOR_YUV2RGB_YUY2);

    constant("COLOR_YUV2RGB_YUYV", +cv::COLOR_YUV2RGB_YUYV);

    constant("COLOR_YUV2RGB_YV12", +cv::COLOR_YUV2RGB_YV12);

    constant("COLOR_YUV2RGB_YVYU", +cv::COLOR_YUV2RGB_YVYU);

    constant("COLOR_YUV420p2BGR", +cv::COLOR_YUV420p2BGR);

    constant("COLOR_YUV420p2BGRA", +cv::COLOR_YUV420p2BGRA);

    constant("COLOR_YUV420p2GRAY", +cv::COLOR_YUV420p2GRAY);

    constant("COLOR_YUV420p2RGB", +cv::COLOR_YUV420p2RGB);

    constant("COLOR_YUV420p2RGBA", +cv::COLOR_YUV420p2RGBA);

    constant("COLOR_YUV420sp2BGR", +cv::COLOR_YUV420sp2BGR);

    constant("COLOR_YUV420sp2BGRA", +cv::COLOR_YUV420sp2BGRA);

    constant("COLOR_YUV420sp2GRAY", +cv::COLOR_YUV420sp2GRAY);

    constant("COLOR_YUV420sp2RGB", +cv::COLOR_YUV420sp2RGB);

    constant("COLOR_YUV420sp2RGBA", +cv::COLOR_YUV420sp2RGBA);

    constant("COLOR_mRGBA2RGBA", +cv::COLOR_mRGBA2RGBA);

    constant("CONTOURS_MATCH_I1", +cv::CONTOURS_MATCH_I1);

    constant("CONTOURS_MATCH_I2", +cv::CONTOURS_MATCH_I2);

    constant("CONTOURS_MATCH_I3", +cv::CONTOURS_MATCH_I3);

    constant("COVAR_COLS", +cv::COVAR_COLS);

    constant("COVAR_NORMAL", +cv::COVAR_NORMAL);

    constant("COVAR_ROWS", +cv::COVAR_ROWS);

    constant("COVAR_SCALE", +cv::COVAR_SCALE);

    constant("COVAR_SCRAMBLED", +cv::COVAR_SCRAMBLED);

    constant("COVAR_USE_AVG", +cv::COVAR_USE_AVG);

    constant("DCT_INVERSE", +cv::DCT_INVERSE);

    constant("DCT_ROWS", +cv::DCT_ROWS);

    constant("DECOMP_CHOLESKY", +cv::DECOMP_CHOLESKY);

    constant("DECOMP_EIG", +cv::DECOMP_EIG);

    constant("DECOMP_LU", +cv::DECOMP_LU);

    constant("DECOMP_NORMAL", +cv::DECOMP_NORMAL);

    constant("DECOMP_QR", +cv::DECOMP_QR);

    constant("DECOMP_SVD", +cv::DECOMP_SVD);

    constant("DFT_COMPLEX_INPUT", +cv::DFT_COMPLEX_INPUT);

    constant("DFT_COMPLEX_OUTPUT", +cv::DFT_COMPLEX_OUTPUT);

    constant("DFT_INVERSE", +cv::DFT_INVERSE);

    constant("DFT_REAL_OUTPUT", +cv::DFT_REAL_OUTPUT);

    constant("DFT_ROWS", +cv::DFT_ROWS);

    constant("DFT_SCALE", +cv::DFT_SCALE);

    constant("DIST_C", +cv::DIST_C);

    constant("DIST_FAIR", +cv::DIST_FAIR);

    constant("DIST_HUBER", +cv::DIST_HUBER);

    constant("DIST_L1", +cv::DIST_L1);

    constant("DIST_L12", +cv::DIST_L12);

    constant("DIST_L2", +cv::DIST_L2);

    constant("DIST_LABEL_CCOMP", +cv::DIST_LABEL_CCOMP);

    constant("DIST_LABEL_PIXEL", +cv::DIST_LABEL_PIXEL);

    constant("DIST_MASK_3", +cv::DIST_MASK_3);

    constant("DIST_MASK_5", +cv::DIST_MASK_5);

    constant("DIST_MASK_PRECISE", +cv::DIST_MASK_PRECISE);

    constant("DIST_USER", +cv::DIST_USER);

    constant("DIST_WELSCH", +cv::DIST_WELSCH);

    constant("FILLED", +cv::FILLED);

    constant("FLOODFILL_FIXED_RANGE", +cv::FLOODFILL_FIXED_RANGE);

    constant("FLOODFILL_MASK_ONLY", +cv::FLOODFILL_MASK_ONLY);

    constant("FONT_HERSHEY_COMPLEX", +cv::FONT_HERSHEY_COMPLEX);

    constant("FONT_HERSHEY_COMPLEX_SMALL", +cv::FONT_HERSHEY_COMPLEX_SMALL);

    constant("FONT_HERSHEY_DUPLEX", +cv::FONT_HERSHEY_DUPLEX);

    constant("FONT_HERSHEY_PLAIN", +cv::FONT_HERSHEY_PLAIN);

    constant("FONT_HERSHEY_SCRIPT_COMPLEX", +cv::FONT_HERSHEY_SCRIPT_COMPLEX);

    constant("FONT_HERSHEY_SCRIPT_SIMPLEX", +cv::FONT_HERSHEY_SCRIPT_SIMPLEX);

    constant("FONT_HERSHEY_SIMPLEX", +cv::FONT_HERSHEY_SIMPLEX);

    constant("FONT_HERSHEY_TRIPLEX", +cv::FONT_HERSHEY_TRIPLEX);

    constant("FONT_ITALIC", +cv::FONT_ITALIC);

    constant("FileNode_EMPTY", +cv::FileNode::EMPTY);

    constant("FileNode_FLOAT", +cv::FileNode::FLOAT);

    constant("FileNode_FLOW", +cv::FileNode::FLOW);

    constant("FileNode_INT", +cv::FileNode::INT);

    constant("FileNode_MAP", +cv::FileNode::MAP);

    constant("FileNode_NAMED", +cv::FileNode::NAMED);

    constant("FileNode_NONE", +cv::FileNode::NONE);

    constant("FileNode_REAL", +cv::FileNode::REAL);

    constant("FileNode_REF", +cv::FileNode::REF);

    constant("FileNode_SEQ", +cv::FileNode::SEQ);

    constant("FileNode_STR", +cv::FileNode::STR);

    constant("FileNode_STRING", +cv::FileNode::STRING);

    constant("FileNode_TYPE_MASK", +cv::FileNode::TYPE_MASK);

    constant("FileNode_USER", +cv::FileNode::USER);

    constant("FileStorage_APPEND", +cv::FileStorage::APPEND);

    constant("FileStorage_BASE64", +cv::FileStorage::BASE64);

    constant("FileStorage_FORMAT_AUTO", +cv::FileStorage::FORMAT_AUTO);

    constant("FileStorage_FORMAT_JSON", +cv::FileStorage::FORMAT_JSON);

    constant("FileStorage_FORMAT_MASK", +cv::FileStorage::FORMAT_MASK);

    constant("FileStorage_FORMAT_XML", +cv::FileStorage::FORMAT_XML);

    constant("FileStorage_FORMAT_YAML", +cv::FileStorage::FORMAT_YAML);

    constant("FileStorage_INSIDE_MAP", +cv::FileStorage::INSIDE_MAP);

    constant("FileStorage_MEMORY", +cv::FileStorage::MEMORY);

    constant("FileStorage_NAME_EXPECTED", +cv::FileStorage::NAME_EXPECTED);

    constant("FileStorage_READ", +cv::FileStorage::READ);

    constant("FileStorage_UNDEFINED", +cv::FileStorage::UNDEFINED);

    constant("FileStorage_VALUE_EXPECTED", +cv::FileStorage::VALUE_EXPECTED);

    constant("FileStorage_WRITE", +cv::FileStorage::WRITE);

    constant("FileStorage_WRITE_BASE64", +cv::FileStorage::WRITE_BASE64);

    constant("Formatter_FMT_C", +cv::Formatter::FMT_C);

    constant("Formatter_FMT_CSV", +cv::Formatter::FMT_CSV);

    constant("Formatter_FMT_DEFAULT", +cv::Formatter::FMT_DEFAULT);

    constant("Formatter_FMT_MATLAB", +cv::Formatter::FMT_MATLAB);

    constant("Formatter_FMT_NUMPY", +cv::Formatter::FMT_NUMPY);

    constant("Formatter_FMT_PYTHON", +cv::Formatter::FMT_PYTHON);

    constant("GC_BGD", +cv::GC_BGD);

    constant("GC_EVAL", +cv::GC_EVAL);

    constant("GC_FGD", +cv::GC_FGD);

    constant("GC_INIT_WITH_MASK", +cv::GC_INIT_WITH_MASK);

    constant("GC_INIT_WITH_RECT", +cv::GC_INIT_WITH_RECT);

    constant("GC_PR_BGD", +cv::GC_PR_BGD);

    constant("GC_PR_FGD", +cv::GC_PR_FGD);

    constant("GEMM_1_T", +cv::GEMM_1_T);

    constant("GEMM_2_T", +cv::GEMM_2_T);

    constant("GEMM_3_T", +cv::GEMM_3_T);

    constant("HISTCMP_BHATTACHARYYA", +cv::HISTCMP_BHATTACHARYYA);

    constant("HISTCMP_CHISQR", +cv::HISTCMP_CHISQR);

    constant("HISTCMP_CHISQR_ALT", +cv::HISTCMP_CHISQR_ALT);

    constant("HISTCMP_CORREL", +cv::HISTCMP_CORREL);

    constant("HISTCMP_HELLINGER", +cv::HISTCMP_HELLINGER);

    constant("HISTCMP_INTERSECT", +cv::HISTCMP_INTERSECT);

    constant("HISTCMP_KL_DIV", +cv::HISTCMP_KL_DIV);

    constant("HOGDescriptor_DEFAULT_NLEVELS", +cv::HOGDescriptor::DEFAULT_NLEVELS);

    constant("HOGDescriptor_L2Hys", +cv::HOGDescriptor::L2Hys);

    constant("HOUGH_GRADIENT", +cv::HOUGH_GRADIENT);

    constant("HOUGH_MULTI_SCALE", +cv::HOUGH_MULTI_SCALE);

    constant("HOUGH_PROBABILISTIC", +cv::HOUGH_PROBABILISTIC);

    constant("HOUGH_STANDARD", +cv::HOUGH_STANDARD);

    constant("Hamming_normType", +cv::Hamming::normType);

    constant("INTERSECT_FULL", +cv::INTERSECT_FULL);

    constant("INTERSECT_NONE", +cv::INTERSECT_NONE);

    constant("INTERSECT_PARTIAL", +cv::INTERSECT_PARTIAL);

    constant("INTER_AREA", +cv::INTER_AREA);

    constant("INTER_BITS", +cv::INTER_BITS);

    constant("INTER_BITS2", +cv::INTER_BITS2);

    constant("INTER_CUBIC", +cv::INTER_CUBIC);

    constant("INTER_LANCZOS4", +cv::INTER_LANCZOS4);

    constant("INTER_LINEAR", +cv::INTER_LINEAR);

    constant("INTER_MAX", +cv::INTER_MAX);

    constant("INTER_NEAREST", +cv::INTER_NEAREST);

    constant("INTER_TAB_SIZE", +cv::INTER_TAB_SIZE);

    constant("INTER_TAB_SIZE2", +cv::INTER_TAB_SIZE2);

    constant("KMEANS_PP_CENTERS", +cv::KMEANS_PP_CENTERS);

    constant("KMEANS_RANDOM_CENTERS", +cv::KMEANS_RANDOM_CENTERS);

    constant("KMEANS_USE_INITIAL_LABELS", +cv::KMEANS_USE_INITIAL_LABELS);

    constant("LINE_4", +cv::LINE_4);

    constant("LINE_8", +cv::LINE_8);

    constant("LINE_AA", +cv::LINE_AA);

    constant("LSD_REFINE_ADV", +cv::LSD_REFINE_ADV);

    constant("LSD_REFINE_NONE", +cv::LSD_REFINE_NONE);

    constant("LSD_REFINE_STD", +cv::LSD_REFINE_STD);

    constant("MARKER_CROSS", +cv::MARKER_CROSS);

    constant("MARKER_DIAMOND", +cv::MARKER_DIAMOND);

    constant("MARKER_SQUARE", +cv::MARKER_SQUARE);

    constant("MARKER_STAR", +cv::MARKER_STAR);

    constant("MARKER_TILTED_CROSS", +cv::MARKER_TILTED_CROSS);

    constant("MARKER_TRIANGLE_DOWN", +cv::MARKER_TRIANGLE_DOWN);

    constant("MARKER_TRIANGLE_UP", +cv::MARKER_TRIANGLE_UP);

    constant("MORPH_BLACKHAT", +cv::MORPH_BLACKHAT);

    constant("MORPH_CLOSE", +cv::MORPH_CLOSE);

    constant("MORPH_CROSS", +cv::MORPH_CROSS);

    constant("MORPH_DILATE", +cv::MORPH_DILATE);

    constant("MORPH_ELLIPSE", +cv::MORPH_ELLIPSE);

    constant("MORPH_ERODE", +cv::MORPH_ERODE);

    constant("MORPH_GRADIENT", +cv::MORPH_GRADIENT);

    constant("MORPH_HITMISS", +cv::MORPH_HITMISS);

    constant("MORPH_OPEN", +cv::MORPH_OPEN);

    constant("MORPH_RECT", +cv::MORPH_RECT);

    constant("MORPH_TOPHAT", +cv::MORPH_TOPHAT);

    constant("MOTION_AFFINE", +cv::MOTION_AFFINE);

    constant("MOTION_EUCLIDEAN", +cv::MOTION_EUCLIDEAN);

    constant("MOTION_HOMOGRAPHY", +cv::MOTION_HOMOGRAPHY);

    constant("MOTION_TRANSLATION", +cv::MOTION_TRANSLATION);

    constant("Mat_AUTO_STEP", +cv::Mat::AUTO_STEP);

    constant("Mat_CONTINUOUS_FLAG", +cv::Mat::CONTINUOUS_FLAG);

    constant("Mat_DEPTH_MASK", +cv::Mat::DEPTH_MASK);

    constant("Mat_MAGIC_MASK", +cv::Mat::MAGIC_MASK);

    constant("Mat_MAGIC_VAL", +cv::Mat::MAGIC_VAL);

    constant("Mat_SUBMATRIX_FLAG", +cv::Mat::SUBMATRIX_FLAG);

    constant("Mat_TYPE_MASK", +cv::Mat::TYPE_MASK);

    constant("NORM_HAMMING", +cv::NORM_HAMMING);

    constant("NORM_HAMMING2", +cv::NORM_HAMMING2);

    constant("NORM_INF", +cv::NORM_INF);

    constant("NORM_L1", +cv::NORM_L1);

    constant("NORM_L2", +cv::NORM_L2);

    constant("NORM_L2SQR", +cv::NORM_L2SQR);

    constant("NORM_MINMAX", +cv::NORM_MINMAX);

    constant("NORM_RELATIVE", +cv::NORM_RELATIVE);

    constant("NORM_TYPE_MASK", +cv::NORM_TYPE_MASK);

    constant("OPTFLOW_FARNEBACK_GAUSSIAN", +cv::OPTFLOW_FARNEBACK_GAUSSIAN);

    constant("OPTFLOW_LK_GET_MIN_EIGENVALS", +cv::OPTFLOW_LK_GET_MIN_EIGENVALS);

    constant("OPTFLOW_USE_INITIAL_FLOW", +cv::OPTFLOW_USE_INITIAL_FLOW);

    constant("PCA_DATA_AS_COL", +cv::PCA::DATA_AS_COL);

    constant("PCA_DATA_AS_ROW", +cv::PCA::DATA_AS_ROW);

    constant("PCA_USE_AVG", +cv::PCA::USE_AVG);

    constant("PROJ_SPHERICAL_EQRECT", +cv::PROJ_SPHERICAL_EQRECT);

    constant("PROJ_SPHERICAL_ORTHO", +cv::PROJ_SPHERICAL_ORTHO);

    constant("Param_ALGORITHM", +cv::Param::ALGORITHM);

    constant("Param_BOOLEAN", +cv::Param::BOOLEAN);

    constant("Param_FLOAT", +cv::Param::FLOAT);

    constant("Param_INT", +cv::Param::INT);

    constant("Param_MAT", +cv::Param::MAT);

    constant("Param_MAT_VECTOR", +cv::Param::MAT_VECTOR);

    constant("Param_REAL", +cv::Param::REAL);

    constant("Param_STRING", +cv::Param::STRING);

    constant("Param_UCHAR", +cv::Param::UCHAR);

    constant("Param_UINT64", +cv::Param::UINT64);

    constant("Param_UNSIGNED_INT", +cv::Param::UNSIGNED_INT);

    constant("REDUCE_AVG", +cv::REDUCE_AVG);

    constant("REDUCE_MAX", +cv::REDUCE_MAX);

    constant("REDUCE_MIN", +cv::REDUCE_MIN);

    constant("REDUCE_SUM", +cv::REDUCE_SUM);

    constant("RETR_CCOMP", +cv::RETR_CCOMP);

    constant("RETR_EXTERNAL", +cv::RETR_EXTERNAL);

    constant("RETR_FLOODFILL", +cv::RETR_FLOODFILL);

    constant("RETR_LIST", +cv::RETR_LIST);

    constant("RETR_TREE", +cv::RETR_TREE);

    constant("RNG_NORMAL", +cv::RNG::NORMAL);

    constant("RNG_UNIFORM", +cv::RNG::UNIFORM);

    constant("ROTATE_180", +cv::ROTATE_180);

    constant("ROTATE_90_CLOCKWISE", +cv::ROTATE_90_CLOCKWISE);

    constant("ROTATE_90_COUNTERCLOCKWISE", +cv::ROTATE_90_COUNTERCLOCKWISE);

    constant("SOLVELP_MULTI", +cv::SOLVELP_MULTI);

    constant("SOLVELP_SINGLE", +cv::SOLVELP_SINGLE);

    constant("SOLVELP_UNBOUNDED", +cv::SOLVELP_UNBOUNDED);

    constant("SOLVELP_UNFEASIBLE", +cv::SOLVELP_UNFEASIBLE);

    constant("SORT_ASCENDING", +cv::SORT_ASCENDING);

    constant("SORT_DESCENDING", +cv::SORT_DESCENDING);

    constant("SORT_EVERY_COLUMN", +cv::SORT_EVERY_COLUMN);

    constant("SORT_EVERY_ROW", +cv::SORT_EVERY_ROW);

    constant("SVD_FULL_UV", +cv::SVD::FULL_UV);

    constant("SVD_MODIFY_A", +cv::SVD::MODIFY_A);

    constant("SVD_NO_UV", +cv::SVD::NO_UV);

    constant("SparseMat_HASH_BIT", +cv::SparseMat::HASH_BIT);

    constant("SparseMat_HASH_SCALE", +cv::SparseMat::HASH_SCALE);

    constant("SparseMat_MAGIC_VAL", +cv::SparseMat::MAGIC_VAL);

    constant("SparseMat_MAX_DIM", +cv::SparseMat::MAX_DIM);

    constant("Subdiv2D_NEXT_AROUND_DST", +cv::Subdiv2D::NEXT_AROUND_DST);

    constant("Subdiv2D_NEXT_AROUND_LEFT", +cv::Subdiv2D::NEXT_AROUND_LEFT);

    constant("Subdiv2D_NEXT_AROUND_ORG", +cv::Subdiv2D::NEXT_AROUND_ORG);

    constant("Subdiv2D_NEXT_AROUND_RIGHT", +cv::Subdiv2D::NEXT_AROUND_RIGHT);

    constant("Subdiv2D_PREV_AROUND_DST", +cv::Subdiv2D::PREV_AROUND_DST);

    constant("Subdiv2D_PREV_AROUND_LEFT", +cv::Subdiv2D::PREV_AROUND_LEFT);

    constant("Subdiv2D_PREV_AROUND_ORG", +cv::Subdiv2D::PREV_AROUND_ORG);

    constant("Subdiv2D_PREV_AROUND_RIGHT", +cv::Subdiv2D::PREV_AROUND_RIGHT);

    constant("Subdiv2D_PTLOC_ERROR", +cv::Subdiv2D::PTLOC_ERROR);

    constant("Subdiv2D_PTLOC_INSIDE", +cv::Subdiv2D::PTLOC_INSIDE);

    constant("Subdiv2D_PTLOC_ON_EDGE", +cv::Subdiv2D::PTLOC_ON_EDGE);

    constant("Subdiv2D_PTLOC_OUTSIDE_RECT", +cv::Subdiv2D::PTLOC_OUTSIDE_RECT);

    constant("Subdiv2D_PTLOC_VERTEX", +cv::Subdiv2D::PTLOC_VERTEX);

    constant("THRESH_BINARY", +cv::THRESH_BINARY);

    constant("THRESH_BINARY_INV", +cv::THRESH_BINARY_INV);

    constant("THRESH_MASK", +cv::THRESH_MASK);

    constant("THRESH_OTSU", +cv::THRESH_OTSU);

    constant("THRESH_TOZERO", +cv::THRESH_TOZERO);

    constant("THRESH_TOZERO_INV", +cv::THRESH_TOZERO_INV);

    constant("THRESH_TRIANGLE", +cv::THRESH_TRIANGLE);

    constant("THRESH_TRUNC", +cv::THRESH_TRUNC);

    constant("TM_CCOEFF", +cv::TM_CCOEFF);

    constant("TM_CCOEFF_NORMED", +cv::TM_CCOEFF_NORMED);

    constant("TM_CCORR", +cv::TM_CCORR);

    constant("TM_CCORR_NORMED", +cv::TM_CCORR_NORMED);

    constant("TM_SQDIFF", +cv::TM_SQDIFF);

    constant("TM_SQDIFF_NORMED", +cv::TM_SQDIFF_NORMED);

    constant("TermCriteria_COUNT", +cv::TermCriteria::COUNT);

    constant("TermCriteria_EPS", +cv::TermCriteria::EPS);

    constant("TermCriteria_MAX_ITER", +cv::TermCriteria::MAX_ITER);

    constant("UMatData_ASYNC_CLEANUP", +cv::UMatData::ASYNC_CLEANUP);

    constant("UMatData_COPY_ON_MAP", +cv::UMatData::COPY_ON_MAP);

    constant("UMatData_DEVICE_COPY_OBSOLETE", +cv::UMatData::DEVICE_COPY_OBSOLETE);

    constant("UMatData_DEVICE_MEM_MAPPED", +cv::UMatData::DEVICE_MEM_MAPPED);

    constant("UMatData_HOST_COPY_OBSOLETE", +cv::UMatData::HOST_COPY_OBSOLETE);

    constant("UMatData_TEMP_COPIED_UMAT", +cv::UMatData::TEMP_COPIED_UMAT);

    constant("UMatData_TEMP_UMAT", +cv::UMatData::TEMP_UMAT);

    constant("UMatData_USER_ALLOCATED", +cv::UMatData::USER_ALLOCATED);

    constant("UMat_AUTO_STEP", +cv::UMat::AUTO_STEP);

    constant("UMat_CONTINUOUS_FLAG", +cv::UMat::CONTINUOUS_FLAG);

    constant("UMat_DEPTH_MASK", +cv::UMat::DEPTH_MASK);

    constant("UMat_MAGIC_MASK", +cv::UMat::MAGIC_MASK);

    constant("UMat_MAGIC_VAL", +cv::UMat::MAGIC_VAL);

    constant("UMat_SUBMATRIX_FLAG", +cv::UMat::SUBMATRIX_FLAG);

    constant("UMat_TYPE_MASK", +cv::UMat::TYPE_MASK);

    constant("USAGE_ALLOCATE_DEVICE_MEMORY", +cv::USAGE_ALLOCATE_DEVICE_MEMORY);

    constant("USAGE_ALLOCATE_HOST_MEMORY", +cv::USAGE_ALLOCATE_HOST_MEMORY);

    constant("USAGE_ALLOCATE_SHARED_MEMORY", +cv::USAGE_ALLOCATE_SHARED_MEMORY);

    constant("USAGE_DEFAULT", +cv::USAGE_DEFAULT);

    constant("WARP_FILL_OUTLIERS", +cv::WARP_FILL_OUTLIERS);

    constant("WARP_INVERSE_MAP", +cv::WARP_INVERSE_MAP);

    constant("_InputArray_CUDA_GPU_MAT", +cv::_InputArray::CUDA_GPU_MAT);

    constant("_InputArray_CUDA_HOST_MEM", +cv::_InputArray::CUDA_HOST_MEM);

    constant("_InputArray_EXPR", +cv::_InputArray::EXPR);

    constant("_InputArray_FIXED_SIZE", +cv::_InputArray::FIXED_SIZE);

    constant("_InputArray_FIXED_TYPE", +cv::_InputArray::FIXED_TYPE);

    constant("_InputArray_KIND_MASK", +cv::_InputArray::KIND_MASK);

    constant("_InputArray_KIND_SHIFT", +cv::_InputArray::KIND_SHIFT);

    constant("_InputArray_MAT", +cv::_InputArray::MAT);

    constant("_InputArray_MATX", +cv::_InputArray::MATX);

    constant("_InputArray_NONE", +cv::_InputArray::NONE);

    constant("_InputArray_OPENGL_BUFFER", +cv::_InputArray::OPENGL_BUFFER);

    constant("_InputArray_STD_ARRAY", +cv::_InputArray::STD_ARRAY);

    constant("_InputArray_STD_ARRAY_MAT", +cv::_InputArray::STD_ARRAY_MAT);

    constant("_InputArray_STD_BOOL_VECTOR", +cv::_InputArray::STD_BOOL_VECTOR);

    constant("_InputArray_STD_VECTOR", +cv::_InputArray::STD_VECTOR);

    constant("_InputArray_STD_VECTOR_CUDA_GPU_MAT", +cv::_InputArray::STD_VECTOR_CUDA_GPU_MAT);

    constant("_InputArray_STD_VECTOR_MAT", +cv::_InputArray::STD_VECTOR_MAT);

    constant("_InputArray_STD_VECTOR_UMAT", +cv::_InputArray::STD_VECTOR_UMAT);

    constant("_InputArray_STD_VECTOR_VECTOR", +cv::_InputArray::STD_VECTOR_VECTOR);

    constant("_InputArray_UMAT", +cv::_InputArray::UMAT);

    constant("_OutputArray_DEPTH_MASK_16S", +cv::_OutputArray::DEPTH_MASK_16S);

    constant("_OutputArray_DEPTH_MASK_16U", +cv::_OutputArray::DEPTH_MASK_16U);

    constant("_OutputArray_DEPTH_MASK_32F", +cv::_OutputArray::DEPTH_MASK_32F);

    constant("_OutputArray_DEPTH_MASK_32S", +cv::_OutputArray::DEPTH_MASK_32S);

    constant("_OutputArray_DEPTH_MASK_64F", +cv::_OutputArray::DEPTH_MASK_64F);

    constant("_OutputArray_DEPTH_MASK_8S", +cv::_OutputArray::DEPTH_MASK_8S);

    constant("_OutputArray_DEPTH_MASK_8U", +cv::_OutputArray::DEPTH_MASK_8U);

    constant("_OutputArray_DEPTH_MASK_ALL", +cv::_OutputArray::DEPTH_MASK_ALL);

    constant("_OutputArray_DEPTH_MASK_ALL_BUT_8S", +cv::_OutputArray::DEPTH_MASK_ALL_BUT_8S);

    constant("_OutputArray_DEPTH_MASK_FLT", +cv::_OutputArray::DEPTH_MASK_FLT);

    constant("__UMAT_USAGE_FLAGS_32BIT", +cv::__UMAT_USAGE_FLAGS_32BIT);

    constant("BadAlign", +cv::Error::BadAlign);

    constant("BadAlphaChannel", +cv::Error::BadAlphaChannel);

    constant("BadCOI", +cv::Error::BadCOI);

    constant("BadCallBack", +cv::Error::BadCallBack);

    constant("BadDataPtr", +cv::Error::BadDataPtr);

    constant("BadDepth", +cv::Error::BadDepth);

    constant("BadImageSize", +cv::Error::BadImageSize);

    constant("BadModelOrChSeq", +cv::Error::BadModelOrChSeq);

    constant("BadNumChannel1U", +cv::Error::BadNumChannel1U);

    constant("BadNumChannels", +cv::Error::BadNumChannels);

    constant("BadOffset", +cv::Error::BadOffset);

    constant("BadOrder", +cv::Error::BadOrder);

    constant("BadOrigin", +cv::Error::BadOrigin);

    constant("BadROISize", +cv::Error::BadROISize);

    constant("BadStep", +cv::Error::BadStep);

    constant("BadTileSize", +cv::Error::BadTileSize);

    constant("GpuApiCallError", +cv::Error::GpuApiCallError);

    constant("GpuNotSupported", +cv::Error::GpuNotSupported);

    constant("HeaderIsNull", +cv::Error::HeaderIsNull);

    constant("MaskIsTiled", +cv::Error::MaskIsTiled);

    constant("OpenCLApiCallError", +cv::Error::OpenCLApiCallError);

    constant("OpenCLDoubleNotSupported", +cv::Error::OpenCLDoubleNotSupported);

    constant("OpenCLInitError", +cv::Error::OpenCLInitError);

    constant("OpenCLNoAMDBlasFft", +cv::Error::OpenCLNoAMDBlasFft);

    constant("OpenGlApiCallError", +cv::Error::OpenGlApiCallError);

    constant("OpenGlNotSupported", +cv::Error::OpenGlNotSupported);

    constant("StsAssert", +cv::Error::StsAssert);

    constant("StsAutoTrace", +cv::Error::StsAutoTrace);

    constant("StsBackTrace", +cv::Error::StsBackTrace);

    constant("StsBadArg", +cv::Error::StsBadArg);

    constant("StsBadFlag", +cv::Error::StsBadFlag);

    constant("StsBadFunc", +cv::Error::StsBadFunc);

    constant("StsBadMask", +cv::Error::StsBadMask);

    constant("StsBadMemBlock", +cv::Error::StsBadMemBlock);

    constant("StsBadPoint", +cv::Error::StsBadPoint);

    constant("StsBadSize", +cv::Error::StsBadSize);

    constant("StsDivByZero", +cv::Error::StsDivByZero);

    constant("StsError", +cv::Error::StsError);

    constant("StsFilterOffsetErr", +cv::Error::StsFilterOffsetErr);

    constant("StsFilterStructContentErr", +cv::Error::StsFilterStructContentErr);

    constant("StsInplaceNotSupported", +cv::Error::StsInplaceNotSupported);

    constant("StsInternal", +cv::Error::StsInternal);

    constant("StsKernelStructContentErr", +cv::Error::StsKernelStructContentErr);

    constant("StsNoConv", +cv::Error::StsNoConv);

    constant("StsNoMem", +cv::Error::StsNoMem);

    constant("StsNotImplemented", +cv::Error::StsNotImplemented);

    constant("StsNullPtr", +cv::Error::StsNullPtr);

    constant("StsObjectNotFound", +cv::Error::StsObjectNotFound);

    constant("StsOk", +cv::Error::StsOk);

    constant("StsOutOfRange", +cv::Error::StsOutOfRange);

    constant("StsParseError", +cv::Error::StsParseError);

    constant("StsUnmatchedFormats", +cv::Error::StsUnmatchedFormats);

    constant("StsUnmatchedSizes", +cv::Error::StsUnmatchedSizes);

    constant("StsUnsupportedFormat", +cv::Error::StsUnsupportedFormat);

    constant("StsVecLengthErr", +cv::Error::StsVecLengthErr);

    constant("FLAGS_EXPAND_SAME_NAMES", +cv::instr::FLAGS_EXPAND_SAME_NAMES);

    constant("FLAGS_MAPPING", +cv::instr::FLAGS_MAPPING);

    constant("FLAGS_NONE", +cv::instr::FLAGS_NONE);

    constant("IMPL_IPP", +cv::instr::IMPL_IPP);

    constant("IMPL_OPENCL", +cv::instr::IMPL_OPENCL);

    constant("IMPL_PLAIN", +cv::instr::IMPL_PLAIN);

    constant("TYPE_FUN", +cv::instr::TYPE_FUN);

    constant("TYPE_GENERAL", +cv::instr::TYPE_GENERAL);

    constant("TYPE_MARKER", +cv::instr::TYPE_MARKER);

    constant("TYPE_WRAPPER", +cv::instr::TYPE_WRAPPER);

}
